import {PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {Button} from '@/components/ui/button/Button'
import {useTranslation} from '@/translations/i18n'
import {useState, useRef, ChangeEvent} from 'react'
import {Helmet} from 'react-helmet'
import {FiltersBar} from '@components/ui/filters-bar/FiltersBar.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {UserGroupsGrid} from '@/features/user-groups/components/user-groups-grid/UserGroupsGrid'
import {UserGroupModal} from '@/features/user-groups/components/user-group-modal/UserGroupModal'
import {DeleteUserGroupModal} from '@/features/user-groups/components/delete-user-group-modal/DeleteUserGroupModal'
import {TableInputSearch} from '@/components/commons/table-input-search/TableInputSearch'
import {StyledUserGroupsWrapper} from './style'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {useGetUserGroups} from '@/features/user-groups/queries/useGetUserGroups'
import {useDeleteUserGroup} from '@/features/user-groups/queries/useDeleteUserGroup'
import {UserGroup, UserGroupsSearchParams} from '@/features/user-groups/types'
import {MUTATION_KEYS, QUERY_KEYS, queryClient} from '@/queryClient'
import {debounce} from '@/utilities/helpers'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {usePermissions} from '@/features/auth/store/store'
import {useIsMutating} from '@tanstack/react-query'

export const UserGroups = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [editingGroup, setEditingGroup] = useState<UserGroup | undefined>(undefined)
    const [groupToDelete, setGroupToDelete] = useState<UserGroup | undefined>(undefined)
    const inputSearchRef = useRef<HTMLInputElement>(null)

    // Use URL search parameters instead of local state
    const {searchParams, setSearchParams} = useParsedSearchParams(UserGroupsSearchParams)

    // Get user groups data
    const {data: userGroupsData, isLoading: isLoadingGroups} = useGetUserGroups({
        search: searchParams.search
    })

    const userGroups = userGroupsData?.data || []

    //Create user group mutation
    const isCreatingUserGroup = !!useIsMutating({mutationKey: [MUTATION_KEYS.CREATE_USER_GROUP]})
    const isUpdatingUserGroup = !!useIsMutating({mutationKey: [MUTATION_KEYS.UPDATE_USER_GROUP]})

    //Delete user group mutation
    const {mutateAsync: deleteUserGroupAsync, isPending: isDeletingUserGroup} = useDeleteUserGroup({
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USER_GROUPS]})
            setGroupToDelete(undefined)
        }
    })

    // Debounced search function with URL params
    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined})
    }, 500)

    // UI event handlers
    const handleCreateButtonClick = () => {
        setEditingGroup(undefined)
        setIsCreateModalOpen(true)
    }

    //Close create group modal
    const handleModalClose = () => {
        setIsCreateModalOpen(false)
        setEditingGroup(undefined)
    }

    //Open delete modal
    const handleOpenDeleteModal = (group: UserGroup) => {
        setGroupToDelete(group)
    }

    //Close delete modal
    const handleCloseDeleteModal = () => {
        setGroupToDelete(undefined)
    }

    //Confirm delete user group
    const handleConfirmDelete = () => {
        if (groupToDelete) {
            const groupIdToDelete = groupToDelete.id
            setGroupToDelete(undefined)
            deleteUserGroupAsync(groupIdToDelete)
        }
    }

    //Loading state
    const isLoading = isCreatingUserGroup || isUpdatingUserGroup || isDeletingUserGroup

    return (
        <StyledUserGroupsWrapper>
            <Helmet title={t('commons:user_groups')} />
            <PageHeading justify="space-between">
                <h2>{t('commons:user_groups')}</h2>
                {permissions?.users?.write && (
                    <Button
                        variant="primaryNeutral"
                        onClick={handleCreateButtonClick}
                        type="button"
                        disabled={isLoading}
                    >
                        <PlusIcon />
                        {t('commons:group')}
                    </Button>
                )}
            </PageHeading>

            <FiltersBar>
                <Flexbox justify="flex-end">
                    <TableInputSearch
                        ref={inputSearchRef}
                        defaultValue={searchParams.search}
                        onChange={onSearch}
                        placeholder={t('user_groups:search_groups')}
                    />
                </Flexbox>
            </FiltersBar>

            {isLoadingGroups ? (
                <UserGroupsGrid groups={[]} onDeleteGroup={handleOpenDeleteModal} isLoading={true} />
            ) : userGroups.length < 1 ? (
                <EmptyState title={t('user_groups:empty_state_title')} subtitle={t('user_groups:empty_state_subtitle')}>
                    {permissions?.users?.write && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleCreateButtonClick}
                            type="button"
                            disabled={isLoading}
                        >
                            <PlusIcon />
                            {t('user_groups:add_group')}
                        </Button>
                    )}
                </EmptyState>
            ) : (
                <UserGroupsGrid groups={userGroups} onDeleteGroup={handleOpenDeleteModal} isLoading={false} />
            )}

            {isCreateModalOpen && <UserGroupModal onClose={handleModalClose} initialGroup={editingGroup} />}

            {groupToDelete && (
                <DeleteUserGroupModal
                    onClose={handleCloseDeleteModal}
                    onDelete={handleConfirmDelete}
                    group={groupToDelete}
                    isDeleting={isDeletingUserGroup}
                />
            )}
        </StyledUserGroupsWrapper>
    )
}
